import { useEffect, useId, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, AtSign, CircleAlert, Clock, Database, History, Loader2, LockKeyhole, ShieldCheck } from 'lucide-react';
import { BrandLoader, SidebarLockup } from '@/components/brand';
import { AnniversaryFeature } from '@/components/anniversary';
import type { AuthConfig } from '@skhub/contracts';
import { useAuth } from '@/app/auth';
import { api, ApiError } from '@/lib/api';
import { landingFor } from '@/app/routes';
import { finishEntraSignIn, isEntraReturn, startEntraSignIn, takeEntraReturnTo } from '@/app/entra';
import { SignalField } from './SignalField';

export function LoginPage() {
  const { session, signInDev, signInEntra } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const emailId = useId();
  const cfg = useQuery({ queryKey: ['auth-config'], queryFn: () => api.get<AuthConfig>('/auth/config'), retry: 1, staleTime: Infinity });
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(params.get('error'));
  const [expired] = useState(() => {
    const v = sessionStorage.getItem('skhub.expired') === '1';
    sessionStorage.removeItem('skhub.expired');
    return v;
  });

  useEffect(() => {
    document.title = 'Login · Smart Assets';
  }, []);

  // SPA flow: when Microsoft sends the browser back here, trade the ID token for a Hub session.
  // Offered whenever it is configured: the only way in under AUTH_MODE=entra, a second option while testing locally.
  const spa = cfg.data?.entra?.flow === 'spa' ? cfg.data.entra : undefined;
  const serverFlow = cfg.data?.entra?.flow === 'server';
  // Busy from the first render when Microsoft has just sent the user back, so the button cannot start a second sign-in.
  const [entraBusy, setEntraBusy] = useState(isEntraReturn);
  // While a Microsoft response is being completed, show a loading screen rather than flashing the sign-in form.
  // Cleared only when the response turns out unusable or fails; on success the page navigates away.
  const [returning, setReturning] = useState(isEntraReturn);
  useEffect(() => {
    if (!spa) return;
    let cancelled = false;
    (async () => {
      try {
        const idToken = await finishEntraSignIn(spa);
        if (cancelled) return;
        if (!idToken) {
          setReturning(false);
          return;
        }
        setEntraBusy(true);
        const signedIn = await signInEntra(idToken);
        if (!cancelled) navigate(landingFor(signedIn.user.role, takeEntraReturnTo()), { replace: true });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Microsoft sign-in could not be completed. Try again.');
          setReturning(false);
        }
      } finally {
        if (!cancelled) setEntraBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spa?.clientId, spa?.tenantId]);

  const startMicrosoft = async (loginHint?: string) => {
    setError(null);
    if (serverFlow) {
      window.location.assign('/api/v1/auth/entra/login');
      return;
    }
    if (!spa) return;
    setEntraBusy(true);
    try {
      await startEntraSignIn(spa, (location.state as { from?: string } | null)?.from, loginHint);
    } catch {
      setEntraBusy(false);
      setError('Could not reach Microsoft sign-in. Check your connection and try again.');
    }
  };

  if (session) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={landingFor(session.user.role, from)} replace />;
  }

  // Fall through to the form when the response cannot be completed (config failed, or Microsoft sign-in is off).
  if (returning && !cfg.isError && !(cfg.data && !spa && !serverFlow)) return <BrandLoader label="Signing you in with Microsoft" />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError('Enter a valid SmartKargo work email address.');
      return;
    }
    // Under Microsoft sign-in the email only pre-selects the account; Microsoft does the authentication.
    if (cfg.data?.mode === 'entra') {
      await startMicrosoft(email.trim());
      return;
    }
    setBusy(true);
    try {
      const signedIn = await signInDev(email.trim());
      navigate(landingFor(signedIn.user.role, (location.state as { from?: string } | null)?.from), { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not sign you in. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const mode = cfg.data?.mode;

  return (
    // One continuous dark stage: brand story and sign-in share the same field, so no half of the screen sits empty.
    <div className="login-stage scope-dark relative isolate min-h-dvh overflow-hidden text-white">
      <DotField />
      <SignalField />
      <div aria-hidden className="login-aurora pointer-events-none absolute inset-0 -z-10" />
      <div className="relative mx-auto flex min-h-dvh w-full max-w-[1360px] flex-col px-5 py-6 sm:px-10 lg:px-14 lg:py-9">
        <header className="enter flex flex-wrap items-center justify-between gap-4" style={{ ['--d' as string]: '0ms' }}>
          <SidebarLockup size="lg" />
          <p className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.04] py-1.5 pr-3.5 pl-2.5 text-[12px] text-white/75 backdrop-blur-sm sm:text-[13px]">
            <span className="grid size-5 place-items-center rounded-full bg-[#e05b26]/15">
              <ShieldCheck aria-hidden className="size-3.5 text-[#ff8a57]" strokeWidth={2} />
            </span>
            Managed &amp; secured by <span className="font-semibold text-white">SmartKargo IT</span>
          </p>
        </header>

        <div className="grid flex-1 items-center gap-10 py-10 lg:grid-cols-[minmax(0,1fr)_minmax(400px,440px)] lg:gap-14 lg:py-12 xl:gap-24">
          {/* ── Brand story ── */}
          <section aria-label="SmartKargo Smart Assets" className="min-w-0">
            <div className="enter mb-9 sm:mb-11" style={{ ['--d' as string]: '60ms' }}>
              <AnniversaryFeature />
            </div>
            <p className="enter flex items-center gap-2.5 text-[11px] font-semibold tracking-[0.22em] text-[#ff8a57] uppercase" style={{ ['--d' as string]: '120ms' }}>
              <span className="inline-block h-px w-6 bg-[#e05b26]" aria-hidden />
              Enterprise IT asset management
            </p>
            <h1 className="enter mt-3 text-[40px] leading-[1.02] font-semibold tracking-[-0.035em] sm:text-[52px] xl:text-[60px]" style={{ ['--d' as string]: '200ms' }}>
              Smart <span className="login-ink login-ink-flow">Assets</span>
            </h1>
            <p className="enter mt-4 max-w-lg text-[16px] leading-relaxed text-white/65 sm:text-[18px]" style={{ ['--d' as string]: '260ms' }}>
              Every SmartKargo laptop, device and software licence, tracked from procurement to retirement with the same precision we bring to every air cargo shipment.
            </p>
            {/* Chips and journey share one width so their edges line up. */}
            <div className="w-fit max-w-full">
            <ul className="enter mt-7 hidden flex-wrap gap-2 sm:flex" style={{ ['--d' as string]: '340ms' }}>
              {(
                [
                  [LockKeyhole, 'Chain of custody'],
                  [History, 'End-to-end lifecycle'],
                  [ShieldCheck, 'Role-based access'],
                  [Database, 'Live inventory'],
                ] as const
              ).map(([Icon, label]) => (
                <li key={label} className="glass inline-flex h-8 items-center gap-2 rounded-[8px] px-3 text-[12px] text-white/80">
                  <Icon aria-hidden className="size-3.5 text-[#ff8a57]/80" strokeWidth={1.75} /> {label}
                </li>
              ))}
            </ul>

            <div className="enter mt-5 hidden sm:block" style={{ ['--d' as string]: '420ms' }}>
              <AssetJourney />
            </div>
            </div>
          </section>

          {/* ── Sign-in ── */}
          <section aria-label="Sign in" className="relative">
            <Orbit />
            <div className="enter relative mx-auto w-full max-w-[440px]" style={{ ['--d' as string]: '180ms' }}>
              <div className="login-card relative rounded-[24px] p-7 sm:p-9">
                <span aria-hidden className="absolute inset-x-12 top-0 h-px bg-[linear-gradient(90deg,transparent,rgb(255_138_87/0.9),transparent)]" />
                <div className="flex items-start gap-4">
                  <span aria-hidden className="login-key grid size-11 shrink-0 place-items-center rounded-[14px]">
                    <LockKeyhole className="size-[18px] text-[#ff8a57]" strokeWidth={2} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-[26px] leading-tight font-semibold tracking-[-0.025em] text-white">Sign in</h2>
                    <p className="mt-1 text-[14px] leading-snug text-white/60">to SmartKargo Smart Assets</p>
                  </div>
                  {mode === 'development' && (
                    <span className="mt-1 shrink-0 rounded-full border border-[#e5a54a]/30 bg-[#e5a54a]/10 px-2.5 py-1 text-[11px] font-medium text-[#f0c27a]">Test access</span>
                  )}
                </div>

                {expired && !error && (
                  <div role="status" className="animate-pop mt-6 flex gap-2.5 rounded-[12px] border border-white/10 bg-white/[0.04] px-3.5 py-3 text-[13px] text-white/70">
                    <Clock aria-hidden className="mt-0.5 size-4 shrink-0 text-white/50" />
                    Your session ended after a period of inactivity. Sign in again to continue.
                  </div>
                )}
                {error && (
                  <div key={error} role="alert" className="login-shake mt-6 flex gap-2.5 rounded-[12px] border border-[#f07167]/25 bg-[#f07167]/10 px-3.5 py-3 text-[13px] text-[#ffb3ad]">
                    <CircleAlert aria-hidden className="mt-0.5 size-4 shrink-0" />
                    {error}
                  </div>
                )}

                {/* Boarding-pass perforation: the card's header and the form read as one ticket. */}
                <div aria-hidden className="mt-6 flex items-center gap-3">
                  <span className="h-px flex-1 border-t border-dashed border-white/[0.12]" />
                  <PlaneGlyph className="h-3 w-[15px] text-[#ff8a57]/80" />
                  <span className="h-px flex-1 border-t border-dashed border-white/[0.12]" />
                </div>

                <div className="mt-6">
                  {cfg.isPending && (
                    <div className="space-y-3" aria-label="Loading sign-in options" role="status">
                      <div className="skeleton h-4 w-24" />
                      <div className="skeleton h-12 w-full" />
                      <div className="skeleton h-12 w-full" />
                    </div>
                  )}

                  {cfg.isError && (
                    <div role="alert" className="rounded-[12px] border border-white/10 px-3.5 py-3 text-[13px] text-white/70">
                      The Smart Assets service is currently unavailable.{' '}
                      <button className="font-medium text-[#ff8a57] underline-offset-2 hover:underline" onClick={() => cfg.refetch()}>
                        Retry
                      </button>
                    </div>
                  )}

                  {cfg.data && (
                    <form onSubmit={submit} noValidate>
                      <label htmlFor={emailId} className="mb-2 block text-[13px] font-medium text-white/80">
                        Work email
                      </label>
                      <div className="login-field relative">
                        <AtSign aria-hidden className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 text-white/40" />
                        <input
                          id={emailId}
                          type="email"
                          autoComplete="username"
                          inputMode="email"
                          spellCheck={false}
                          required
                          autoFocus
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="name@smartkargo.com"
                          aria-invalid={!!error}
                          aria-describedby={mode === 'development' ? `${emailId}-note` : undefined}
                          className="login-input h-[52px] w-full rounded-[14px] pr-4 pl-11 text-[15px] text-white outline-none placeholder:text-white/30"
                        />
                      </div>
                      <button type="submit" disabled={busy || entraBusy} aria-busy={busy || entraBusy} className="btn btn-brand login-cta mt-4 h-[52px] w-full rounded-[14px] text-[15px] font-semibold">
                        {busy || (entraBusy && mode === 'entra') ? <Loader2 aria-hidden className="size-4 animate-spin" /> : null}
                        {busy || (entraBusy && mode === 'entra') ? 'Signing in…' : 'Sign in'}
                        {!busy && !(entraBusy && mode === 'entra') && <ArrowRight aria-hidden className="nudge size-4" />}
                      </button>

                      {mode === 'development' && (spa || serverFlow) && (
                        <>
                          <div className="my-5 flex items-center gap-3 text-[11px] font-medium tracking-[0.14em] text-white/35 uppercase" aria-hidden>
                            <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
                          </div>
                          <button type="button" onClick={() => void startMicrosoft()} disabled={entraBusy} aria-busy={entraBusy} className="login-secondary flex h-[52px] w-full items-center justify-center gap-2.5 rounded-[14px] text-[14px] font-medium text-white/90">
                            {entraBusy ? <Loader2 aria-hidden className="size-4 animate-spin" /> : <MicrosoftMark />}
                            {entraBusy ? 'Opening Microsoft…' : 'Sign in with Microsoft'}
                          </button>
                        </>
                      )}

                      {mode === 'development' ? (
                        <p id={`${emailId}-note`} className="mt-5 flex gap-2 text-[12px] leading-relaxed text-white/45">
                          <ShieldCheck aria-hidden className="mt-px size-3.5 shrink-0 text-[#e5a54a]" />
                          Temporary email sign-in for testing on this computer only. Production uses Microsoft single sign-on.
                        </p>
                      ) : (
                        <p className="mt-5 flex items-center justify-center gap-2 text-[12px] text-white/45">
                          <MicrosoftMark /> Secured by Microsoft single sign-on
                        </p>
                      )}
                    </form>
                  )}
                </div>
              </div>
              <div className="mt-5 space-y-1 px-2 text-center text-xs text-white/50">
                <p>For access requests or sign-in help, contact the SmartKargo IT Team.</p>
                <p className="text-white/35">Restricted to authorised SmartKargo personnel. All sign-in activity is monitored and logged.</p>
              </div>
            </div>
          </section>
        </div>

        <footer className="enter flex flex-wrap items-center justify-between gap-2 border-t border-white/[0.06] pt-5" style={{ ['--d' as string]: '520ms' }}>
          <p className="text-[11px] text-white/40">© {new Date().getFullYear()} SmartKargo · Smart Assets · Built by SmartKargo IT</p>
          <p className="flex items-center gap-2 text-[11px] text-white/45">
            <span aria-hidden className={`size-1.5 rounded-full ${cfg.isError ? 'bg-[#f07167]' : 'animate-[pulse-dot_2.4s_ease-in-out_infinite] bg-[#3ccf9f]'}`} />
            {cfg.isError ? 'Service unavailable' : cfg.data ? 'All systems operational' : 'Connecting…'}
          </p>
        </footer>
      </div>
    </div>
  );
}

/** Cargo aircraft silhouette, nose to the right; inherits colour from the text. */
function PlaneGlyph({ className }: { className?: string }) {
  return (
    <svg aria-hidden viewBox="-11 -9 22 18" className={className} fill="currentColor">
      <path d={PLANE_PATH} />
    </svg>
  );
}
const PLANE_PATH =
  'M10 0C10-.9 9.1-1.4 8-1.4H2L-3-8h-2.5L-2-1.4h-5L-9-4h-1.5l1.3 4-1.3 4H-9l2-2.6h5L-5.5 8H-3l5-6.6h6c1.1 0 2-.5 2-1.4Z';

const JOURNEY = [
  ['PRC', 'Procured'],
  ['ASG', 'Assigned'],
  ['SVC', 'In service'],
  ['RTN', 'Returned'],
] as const;

/**
 * The asset lifecycle drawn as a flight route: a cargo plane flies stop to stop leaving a contrail, and each
 * stop lights as it passes. Transform-only loop; stills under reduced motion (global rule).
 */
function AssetJourney() {
  return (
    <section aria-label="Asset journey" className="glass rounded-[14px] px-5 pt-4 pb-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] font-semibold tracking-[0.18em] text-white/50 uppercase">Asset journey</p>
        <p className="flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.18em] text-[#3ccf9f] uppercase">
          <span aria-hidden className="size-1.5 animate-[pulse-dot_2.4s_ease-in-out_infinite] rounded-full bg-[#3ccf9f]" />
          Tracked end to end
        </p>
      </div>
      <div className="relative mt-4">
        {/* Route: dashed line between the first and last stop centres, with the plane and its contrail. */}
        <div aria-hidden className="absolute top-[-2px] right-8 left-8 h-4 overflow-hidden">
          <span className="absolute inset-x-0 top-1/2 border-t border-dashed border-white/15" />
          <span className="journey-run absolute inset-0">
            <span className="absolute top-1/2 right-full h-px w-28 bg-[linear-gradient(90deg,transparent,rgb(255_138_87/0.85))]" />
            <PlaneGlyph className="absolute top-1/2 left-0 h-3.5 w-[18px] -translate-x-1/2 -translate-y-1/2 text-[#ff8a57] drop-shadow-[0_0_6px_rgb(255_138_87/0.7)]" />
          </span>
        </div>
        <ol className="relative flex justify-between">
          {JOURNEY.map(([code, label], i) => (
            <li key={code} className="flex w-16 flex-col items-center text-center">
              <span aria-hidden className="journey-stop relative grid size-3 place-items-center rounded-full border border-white/25 bg-[#111419]" style={{ ['--i' as string]: i }}>
                <span className="size-1 rounded-full bg-[#ff8a57]" />
              </span>
              <span className="mt-2.5 text-[15px] leading-none font-semibold tracking-[0.08em] text-white">{code}</span>
              <span className="mt-1 text-[11px] leading-none whitespace-nowrap text-white/50">{label}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/** Concentric rings behind the sign-in card with two cargo planes in orbit: the hub at the centre of the network. */
function Orbit() {
  return (
    <svg aria-hidden className="pointer-events-none absolute top-1/2 left-1/2 -z-[1] hidden h-[760px] w-[760px] -translate-x-1/2 -translate-y-1/2 lg:block" viewBox="-380 -380 760 760">
      <defs>
        <radialGradient id="orbit-core">
          <stop offset="0" stopColor="#e05b26" stopOpacity="0.2" />
          <stop offset="1" stopColor="#e05b26" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle r="300" fill="url(#orbit-core)" />
      {[250, 310, 370].map((r) => (
        <circle key={r} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeDasharray={r === 310 ? '2 6' : undefined} />
      ))}
      {/* Two cargo planes on the rings, noses along their direction of travel. */}
      <g className="orbit-spin" style={{ animationDuration: '48s' }}>
        <path d={PLANE_PATH} transform="translate(310 0) rotate(90)" fill="#ff8a57" />
      </g>
      <g className="orbit-spin" style={{ animationDuration: '72s', animationDirection: 'reverse' }}>
        <path d={PLANE_PATH} transform="translate(0 -370) rotate(180) scale(0.75)" fill="#6aa7f0" opacity="0.8" />
      </g>
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg aria-hidden width="16" height="16" viewBox="0 0 21 21">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

/** Static dot grid and a slowly breathing SmartKargo-orange glow behind the brand panel. */
function DotField() {
  return (
    <svg aria-hidden className="login-glow pointer-events-none absolute inset-0 -z-20 h-full w-full">
      <defs>
        <pattern id="sk-dots" width="24" height="24" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="1" fill="rgba(255,255,255,0.07)" />
        </pattern>
        <radialGradient id="sk-glow" cx="85%" cy="18%" r="60%">
          <stop offset="0%" stopColor="#e05b26" stopOpacity="0.16" />
          <stop offset="100%" stopColor="#e05b26" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#sk-dots)" />
      <rect width="100%" height="100%" fill="url(#sk-glow)" />
    </svg>
  );
}
